import { useEffect, useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import classnames from "classnames"
import { toast } from "react-toastify"
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Progress,
  Row,
  Table,
  TabContent,
  TabPane,
} from "reactstrap"
import PageWrapper from "components/Common/PageWrapper"
import DataCard from "components/Common/DataCard"
import SectionHeader from "components/Common/SectionHeader"
import { useActivityLog, useActivityLogs } from "hooks/useActivityLog"
import { useAuth } from "hooks/authHook"
import {
  useDeleteMoneyReceiptLoan,
  useEditMoneyReceipt,
  useMoneyReceipt,
  useSaveMoneyReceiptLoan,
} from "hooks/useMoneyReceipt"
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"
import { calculateBillTotal } from "utils/accessoryUtils"
import { downloadAndOpenFile } from "api/pdfApi"
import PaymentModal from "./PaymentModal"
import MoneyReceiptLoanAddEdit from "./MoneyReceiptLoanAddEdit"
import MoneyReceiptSectionEditModal from "./MoneyReceiptSectionEditModal"
import {
  useAccountCustomerPayment,
  useCreateAccountCustomerPayment,
  useDeleteAccountCustomerPayment,
  useEditAccountCustomerPayment,
} from "hooks/useAccountCustomerPayment"
import { useToastMessage } from "hooks/useToastMessage"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import CustomerLink from "components/Common/CustomerLink"
import VehicleLink from "components/Common/VehicleLink"
import AccountCustomerPaymentView from "pages/AccountPages/AccountCustomerPayment/AccountCustomerPaymentView"
import ActivityLogView from "pages/SystemPages/ActivityLog/ActivityLogView"
import {
  formatCustomerTypeLabel,
  getCustomerOrganizationText,
  resolveCustomerTypeKey,
} from "helpers/customerOnboarding"
import { convertToWords } from "helpers/number_helpers"
import {
  getLoanTotals,
  LoanFormatPreview,
  LoanInformationDetails,
  LoanInformationMetrics,
  LoanInformationSummary,
  PayorderReceiverCard,
} from "./MoneyReceiptLoanShared"
import MoneyReceiptLoanPdfActions from "./MoneyReceiptLoanPdfActions"
import PrintDateModal from "./PrintDateModal"
import CustomReceiptModal from "./CustomReceiptModal"
import CreatableSelect from "react-select/creatable"
import { useAllMoneyReceiptComplimentaryItems } from "hooks/useComplimentaryItem"
import AccessoriesUsageAddEdit from "pages/AccessoriesPages/AccessoriesUsage/AccessoriesUsageAddEdit"
import { useAccessoriesUsages } from "hooks/useAccessoriesUsage"
import { getAccessoriesUsageBillPdfById } from "api/accessoriesUsageApi"
import "./MoneyReceiptView.css"

const TAB_ITEMS = [
  { id: "payments", label: "Payments", icon: "bx bx-credit-card" },
  { id: "agreement", label: "Booking Agreement", icon: "bx bx-receipt" },
  { id: "loan", label: "Loan", icon: "bx bx-buildings" },
  { id: "print", label: "Print", icon: "bx bx-printer" },
  {
    id: "after-sale-service",
    label: "After Sale Service",
    icon: "bx bx-wrench",
  },
]

const getPaymentBadgeColor = status => {
  if (status === "overpaid") return "primary"
  if (status === "fully_paid") return "success"
  if (status === "partially_paid") return "info"
  if (status === "refunded") return "secondary"
  return "warning"
}

const getAdditionalCosts = moneyReceipt => {
  const costs =
    (moneyReceipt &&
      moneyReceipt.pdf_content &&
      moneyReceipt.pdf_content.additional_costs) ||
    []

  return costs.filter(
    item =>
      String((item && item.name) || "").trim() ||
      Number((item && item.amount) || 0) > 0
  )
}

const getComplimentaryItems = moneyReceipt => {
  return moneyReceipt?.complimentary_items || []
}

const getDisplayText = value => {
  if (!value) return ""
  if (typeof value === "string") return value
  if (typeof value === "object") {
    return value?.name || value?.title || value?.label || ""
  }
  return String(value)
}

const formatLabelText = value =>
  String(value || "-")
    .split("_")
    .join(" ")
    .replace(/\b\w/g, char => char.toUpperCase())

const formatWords = amount => {
  const numericAmount = Math.max(0, Math.round(Number(amount) || 0))
  return `${convertToWords(numericAmount)} Taka Only`
}

const CUSTOMER_TYPE_OPTIONS = [
  { value: "general", label: "General" },
  { value: "service", label: "Service" },
  { value: "banker", label: "Banker" },
]

const createEmptyAdditionalCost = () => ({
  name: "",
  amount: "",
})

const normalizeAdditionalCostRows = additionalCosts => {
  if (!Array.isArray(additionalCosts) || additionalCosts.length === 0) {
    return [createEmptyAdditionalCost()]
  }

  const rows = additionalCosts.map(item => ({
    name: item?.name || "",
    amount: item?.amount ?? "",
  }))

  return rows.length ? rows : [createEmptyAdditionalCost()]
}

const getSafeValue = value => {
  if (value === null || value === undefined || value === "") return "N/A"
  return value
}

const getSectionInitialValues = (
  moneyReceipt,
  sectionType,
  customerInfo,
  vehicleInfo
) => {
  const pdfCustomer = moneyReceipt?.pdf_content?.customer_info || {}
  const pdfVehicle = moneyReceipt?.pdf_content?.vehicle_info || {}

  if (sectionType === "agreement") {
    return {
      car_price: moneyReceipt?.car_price ?? "",
      registration_cost: moneyReceipt?.registration_cost ?? "",
      discount: pdfVehicle?.discount ?? vehicleInfo?.discount ?? "",
      additional_costs: normalizeAdditionalCostRows(
        moneyReceipt?.pdf_content?.additional_costs || []
      ),
      complimentary_items: getComplimentaryItems(moneyReceipt),
      edit_reason: "",
    }
  }

  if (sectionType === "customer") {
    return {
      name: pdfCustomer?.name || customerInfo?.name || "",
      mobile: pdfCustomer?.mobile || customerInfo?.mobile || "",
      email: pdfCustomer?.email || customerInfo?.email || "",
      customer_type: resolveCustomerTypeKey(
        pdfCustomer?.customer_type || customerInfo?.customerTypeKey || "general"
      ),
      designation: pdfCustomer?.designation || "",
      company_name: pdfCustomer?.company_name || "",
      bank_name: pdfCustomer?.bank_name || "",
      branch_name: pdfCustomer?.branch_name || "",
      address: pdfCustomer?.address || customerInfo?.address || "",
      edit_reason: "",
    }
  }

  return {
    name: pdfVehicle?.name || vehicleInfo?.vehicleName || "",
    chassis_no: pdfVehicle?.chassis_no || vehicleInfo?.chassisNo || "",
    full_engine: pdfVehicle?.full_engine || vehicleInfo?.fullEngine || "",
    brta_reg: pdfVehicle?.brta_reg || vehicleInfo?.brtaReg || "",
    fuel: pdfVehicle?.fuel || vehicleInfo?.fuel || "",
    color: pdfVehicle?.color || vehicleInfo?.color || "",
    cc: pdfVehicle?.cc || vehicleInfo?.cc || "",
    year_model: pdfVehicle?.year_model || vehicleInfo?.year || "",
    edit_reason: "",
  }
}

const validateSectionValues = (sectionType, values, amountPaid) => {
  const errors = {}

  if (sectionType === "agreement") {
    if (values.car_price === "" || Number(values.car_price) < 0) {
      errors.car_price = "Enter a valid car price."
    }
    if (
      values.registration_cost !== "" &&
      Number(values.registration_cost) < 0
    ) {
      errors.registration_cost = "Registration cost cannot be negative."
    }
    if (values.discount !== "" && Number(values.discount) < 0) {
      errors.discount = "Discount cannot be negative."
    }
    ;(values.additional_costs || []).forEach((item, index) => {
      if ((item?.name || "").trim() || String(item?.amount || "").trim()) {
        if (!String(item?.name || "").trim()) {
          errors[`additional_cost_name_${index}`] = "Cost name is required."
        }
        if (item?.amount === "" || Number(item?.amount) < 0) {
          errors[`additional_cost_amount_${index}`] =
            "Enter a valid cost amount."
        }
      }
    })
  }

  if (sectionType === "customer") {
    if (!String(values.name || "").trim()) errors.name = "Name is required."
    if (!String(values.mobile || "").trim())
      errors.mobile = "Mobile is required."
    if (!String(values.customer_type || "").trim()) {
      errors.customer_type = "Customer type is required."
    }
    if (
      values.customer_type === "service" ||
      values.customer_type === "banker"
    ) {
      if (!String(values.designation || "").trim()) {
        errors.designation = "Designation is required."
      }
    }
    if (
      values.customer_type === "service" &&
      !String(values.company_name || "").trim()
    ) {
      errors.company_name = "Company name is required."
    }
    if (
      values.customer_type === "banker" &&
      !String(values.bank_name || "").trim()
    ) {
      errors.bank_name = "Bank name is required."
    }
    if (
      values.customer_type === "banker" &&
      !String(values.branch_name || "").trim()
    ) {
      errors.branch_name = "Branch name is required."
    }
  }

  if (sectionType === "car") {
    if (!String(values.name || "").trim())
      errors.name = "Vehicle name is required."
    if (!String(values.chassis_no || "").trim()) {
      errors.chassis_no = "Chassis number is required."
    }
    if (!String(values.full_engine || "").trim()) {
      errors.full_engine = "Engine number is required."
    }
  }

  if (Number(amountPaid || 0) > 0 && !String(values.edit_reason || "").trim()) {
    errors.edit_reason =
      "Edit reason is required after payment has been received."
  }

  return errors
}

const getLoanInformation = moneyReceipt => {
  const stored = moneyReceipt?.loan_information || {}
  const customerInfo = getReceiptCustomerInfo(moneyReceipt)
  const vehicleInfo = getReceiptVehicleInfo(moneyReceipt)
  const customerTypeKey = customerInfo?.customerTypeKey

  const customerNameFallback =
    customerInfo?.name && customerInfo.name !== "N/A" ? customerInfo.name : ""
  const customerAddressFallback =
    customerInfo?.address && customerInfo.address !== "N/A"
      ? customerInfo.address
      : ""

  return {
    bank_name:
      stored?.bank_name ||
      (customerTypeKey === "banker" ? customerInfo?.organization || "" : ""),
    branch_name:
      stored?.branch_name ||
      (customerTypeKey === "banker" ? customerInfo?.branchName || "" : ""),
    unit_price:
      stored?.unit_price ??
      Number(vehicleInfo?.price ?? moneyReceipt?.car_price ?? 0),
    bank_paid: stored?.bank_paid ?? 0,
    customer_paid:
      stored?.customer_paid ?? Number(moneyReceipt?.amount_paid ?? 0),
    customer_name: stored?.customer_name || customerNameFallback,
    customer_address: stored?.customer_address || customerAddressFallback,
    ac_hp_format: stored?.ac_hp_format || "H/P",
    bank_account_id:
      stored?.bank_account?.id ?? stored?.bank_account_id ?? null,
    payorder_account_name:
      stored?.bank_account?.account_name ?? stored?.payorder_account_name ?? "",
    payorder_account_number:
      stored?.bank_account?.account_number ??
      stored?.payorder_account_number ??
      "",
    payorder_routing_number:
      stored?.bank_account?.routing_number ??
      stored?.payorder_routing_number ??
      "",
    payorder_bank_name:
      stored?.bank_account?.bank_name ?? stored?.payorder_bank_name ?? "",
    payorder_branch_name:
      stored?.bank_account?.branch_name ?? stored?.payorder_branch_name ?? "",
    customer_payment_mode: stored?.customer_payment_mode || "",
    bank_payment_mode: stored?.bank_payment_mode || "",
    customer_payment_remarks: stored?.customer_payment_remarks || "",
    bank_payment_remarks: stored?.bank_payment_remarks || "",
    payorder_receiver_name: stored?.payorder_receiver_name || "",
    include_handover_block: Boolean(stored?.include_handover_block),
  }
}

const getReceiptCustomerInfo = moneyReceipt => {
  const pdfCustomer = moneyReceipt?.pdf_content?.customer_info || {}
  const customer = moneyReceipt?.customer || {}
  const customerTypeKey = resolveCustomerTypeKey(
    pdfCustomer?.customer_type || customer?.customer_type
  )

  return {
    customerTypeKey,
    name: pdfCustomer?.name || customer?.name || "N/A",
    mobile: pdfCustomer?.mobile || customer?.mobile || "N/A",
    email: pdfCustomer?.email || customer?.email || "N/A",
    customerType: formatCustomerTypeLabel(customerTypeKey),
    designation: pdfCustomer?.designation || customer?.designation || "N/A",
    organization:
      pdfCustomer?.bank_name ||
      pdfCustomer?.company_name ||
      getCustomerOrganizationText(customer) ||
      "N/A",
    branchName: pdfCustomer?.branch_name || customer?.branch_name || "N/A",
    address: pdfCustomer?.address || customer?.full_address || "N/A",
    accountName:
      pdfCustomer?.account_name ||
      customer?.account_name ||
      customer?.bank_account_name ||
      "N/A",
  }
}

const getReceiptVehicleInfo = moneyReceipt => {
  const pdfVehicle = moneyReceipt?.pdf_content?.vehicle_info || {}
  const vehicle = moneyReceipt?.vehicle || {}
  const vehicleName =
    pdfVehicle?.name ||
    [
      getDisplayText(vehicle?.brand),
      getDisplayText(vehicle?.model),
      getDisplayText(vehicle?.package),
      getDisplayText(vehicle?.engine_type),
    ]
      .filter(Boolean)
      .join(" ") ||
    "N/A"

  return {
    stockId: pdfVehicle?.stock_id || vehicle?.stock_id || "N/A",
    vehicleName,
    fullChassis: pdfVehicle?.full_chassis || vehicle?.full_chassis || "N/A",
    chassisNo:
      pdfVehicle?.chassis_no ||
      pdfVehicle?.full_chassis ||
      vehicle?.full_chassis ||
      vehicle?.chassis_number ||
      "N/A",
    fullEngine: pdfVehicle?.full_engine || vehicle?.full_engine || "N/A",
    brtaReg: pdfVehicle?.brta_reg || vehicle?.brta_reg_no || "N/A",
    year: pdfVehicle?.year_model || getDisplayText(vehicle?.year) || "N/A",
    color:
      pdfVehicle?.color ||
      getDisplayText(vehicle?.exteriorColor || vehicle?.exterior_color) ||
      "N/A",
    cc:
      pdfVehicle?.cc ||
      getDisplayText(vehicle?.engineCc || vehicle?.engine_c_c) ||
      "N/A",
    fuel:
      pdfVehicle?.fuel ||
      getDisplayText(vehicle?.fuelType || vehicle?.fuel_type) ||
      "N/A",
    importer: vehicle?.importer?.name || "N/A",
    description:
      pdfVehicle?.descriptions ||
      vehicle?.short_descriptions ||
      vehicle?.full_descriptions ||
      "N/A",
    discount: Number(
      pdfVehicle?.discount ??
        (vehicle?.asking_price > vehicle?.discount_price
          ? vehicle.asking_price - vehicle.discount_price
          : 0)
    ),
    price: Number(pdfVehicle?.price ?? moneyReceipt?.car_price ?? 0),
  }
}

const getActionBadgeConfig = action => {
  switch (action) {
    case "create":
      return { color: "success", label: "Created" }
    case "update":
    case "update-reason":
      return { color: "info", label: "Updated" }
    case "create-loan":
      return { color: "success", label: "Loan Created" }
    case "update-loan":
      return { color: "info", label: "Loan Updated" }
    case "delete-loan":
      return { color: "danger", label: "Loan Deleted" }
    case "delete":
      return { color: "danger", label: "Deleted" }
    case "restore":
      return { color: "warning", label: "Restored" }
    default:
      return { color: "secondary", label: action || "Activity" }
  }
}

const getActivityActor = log =>
  log?.causer_user_display ||
  log?.causer_user_snapshot?.label ||
  log?.user?.name ||
  "System"

const getActivitySubject = log => {
  const paymentEntryType = log?.metadata?.payment_entry_type
  if (paymentEntryType === "refund") {
    return log?.subject_label || `Refund #${log?.subject_id || "N/A"}`
  }
  if (paymentEntryType === "payment") {
    return log?.subject_label || `Payment #${log?.subject_id || "N/A"}`
  }
  if (log?.subject_type === "App\\Models\\MoneyReceipt") {
    return log?.subject_label || `Money Receipt #${log?.subject_id || "N/A"}`
  }
  return (
    log?.subject_label || formatLabelText(log?.subject_type?.split("\\").pop())
  )
}

const getActivitySummary = log =>
  log?.metadata?.edit_reason ||
  (typeof log?.description === "string" && log.description.trim()) ||
  getActivitySubject(log) ||
  "Money receipt activity"

const PrintActionButton = ({
  label,
  color = "light",
  onClick,
  disabled,
  loading = false,
  loadingLabel = "Generating...",
}) => (
  <Button
    color={color}
    className="money-receipt-print-button"
    onClick={onClick}
    disabled={disabled || loading}
  >
    <i className={`bx ${loading ? "bx-loader bx-spin" : "bx-printer"} me-1`} />
    {loading ? loadingLabel : label}
  </Button>
)

const getCollectionProgress = (paidAmount, totalAmount) => {
  const paid = Number(paidAmount ?? 0)
  const total = Number(totalAmount ?? 0)
  if (total <= 0) return 0
  return Math.min(100, Math.max(0, (paid / total) * 100))
}

const ReceiptProfileCard = ({
  moneyReceipt,
  customerInfo,
  vehicleInfo,
  paymentStatusLabel,
  collectionProgress,
  amountPaid,
  totalAmount,
  dueAmount,
  overpaidAmount,
  onBack,
  onEdit,
  canEditReceipt,
}) => {
  const navigate = useNavigate()
  const preOrder = moneyReceipt?.pre_order ?? null

  const rows = [
    { label: "Customer", value: customerInfo?.name || "N/A" },
    { label: "Vehicle", value: vehicleInfo?.vehicleName || "N/A" },
    {
      label: "Issued Date",
      value: formatReadableDate(moneyReceipt?.issued_date, "MMM d, y") || "N/A",
    },
    { label: "Quotation", value: moneyReceipt?.quotation?.qid || "N/A" },
    { label: "Showroom", value: moneyReceipt?.showroom?.name || "N/A" },
    {
      label: "Created By",
      value:
        moneyReceipt?.created_by?.name ||
        moneyReceipt?.createdBy?.name ||
        "N/A",
    },
  ]

  const paymentColor = getPaymentBadgeColor(moneyReceipt?.payment_status)
  const statusColor =
    moneyReceipt?.status === "active" ? "success" : "secondary"
  const additionalCosts = getAdditionalCosts(moneyReceipt)
  const additionalCostTotal = additionalCosts.reduce(
    (sum, item) => sum + Number(item?.amount || 0),
    0
  )
  const discountAmount = Number(moneyReceipt?.discount || 0)
  const totalBreakdownRows = [
    { label: "Car Price", value: formatBDT(moneyReceipt?.car_price) },
    {
      label: "Registration",
      value: formatBDT(moneyReceipt?.registration_cost),
    },
    ...(additionalCostTotal > 0
      ? [
          {
            label:
              additionalCosts.length > 1
                ? "Additional Costs"
                : "Additional Cost",
            value: formatBDT(additionalCostTotal),
          },
        ]
      : []),
    ...(discountAmount > 0
      ? [{ label: "Discount", value: `- ${formatBDT(discountAmount)}` }]
      : []),
  ]

  return (
    <Card className="border-0 shadow-sm overflow-hidden mb-4">
      <div className="money-receipt-profile-card__cover">
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div>
            <h5 className="mb-1 text-dark">
              {moneyReceipt?.mr_no || "Money Receipt"}
            </h5>
            <p className="mb-0 text-muted">
              Collection, customer, and document overview
            </p>
            {preOrder ? (
              <Badge
                className="badge-soft-info border-0 mt-1"
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/pre-orders/${preOrder.id}`)}
                title="Open the source pre-order"
              >
                <i className="bx bx-link-alt me-1" />
                Generated from Pre-Order {preOrder.poid}
              </Badge>
            ) : null}
          </div>

          <div className="d-flex gap-2 flex-wrap align-items-center">
            <Badge className={`badge-soft-${paymentColor} border-0`}>
              {paymentStatusLabel}
            </Badge>
            <Badge className={`badge-soft-${statusColor} border-0`}>
              {formatLabelText(moneyReceipt?.status || "active")}
            </Badge>
          </div>
        </div>
      </div>

      <CardBody className="p-4">
        <div className="d-flex flex-wrap gap-2 mb-4">
          <Button color="light" onClick={onBack}>
            <i className="bx bx-arrow-back me-1" />
            Back
          </Button>

          <CanView access={[ACCESS.MONEY_RECEIPT.EDIT]}>
            <Button color="warning" onClick={onEdit} disabled={!canEditReceipt}>
              <i className="bx bx-edit-alt me-1" />
              Edit
            </Button>
          </CanView>
        </div>

        <div className="money-receipt-collection-panel mb-4">
          <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
            <div>
              <div className="money-receipt-collection-panel__percent">
                {collectionProgress.toFixed(0)}%
              </div>
              <small className="text-muted">Collection progress</small>
            </div>
            <div className="text-end">
              <div className="fw-semibold">{formatBDT(amountPaid)}</div>
              <small className="text-muted">Received</small>
            </div>
          </div>
          <Progress value={collectionProgress} color={paymentColor} />
          <div className="mt-3 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted text-uppercase fw-semibold">
                Total Breakdown
              </small>
              <small className="text-muted">{formatBDT(totalAmount)}</small>
            </div>
            <div className="row g-2">
              {totalBreakdownRows.map(item => (
                <div className="col-12 col-md-4" key={item.label}>
                  <div className="money-receipt-mini-stat">
                    <small className="text-muted d-block">{item.label}</small>
                    <span className="fw-semibold">{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="row g-2 mt-3">
            {[
              { label: "Total", value: formatBDT(totalAmount) },
              { label: "Due", value: formatBDT(dueAmount) },
              { label: "Overpaid", value: formatBDT(overpaidAmount) },
            ].map(item => (
              <div className="col-12 col-md-4" key={item.label}>
                <div className="money-receipt-mini-stat">
                  <small className="text-muted d-block">{item.label}</small>
                  <span className="fw-semibold">{item.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

const LeftRailDataCard = ({
  title,
  icon,
  data,
  action = null,
  noDataText = "No Data",
}) => (
  <DataCard
    title={title}
    icon={icon}
    data={data}
    action={action}
    noDataText={noDataText}
  />
)

const MoneyReceiptViewPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [paymentModalMode, setPaymentModalMode] = useState("payment")
  const [viewPaymentId, setViewPaymentId] = useState(null)
  const [selectedActivityId, setSelectedActivityId] = useState(null)
  const [loanEditModal, setLoanEditModal] = useState(false)
  const [sectionEditType, setSectionEditType] = useState(null)
  const [sectionFormValues, setSectionFormValues] = useState({})
  const [sectionFormErrors, setSectionFormErrors] = useState({})
  const [activeLoanPdfKey, setActiveLoanPdfKey] = useState(null)
  const [activePdfKey, setActivePdfKey] = useState(null)
  const [activeAccessoryBillKey, setActiveAccessoryBillKey] = useState(null)
  const [accessoryModalOpen, setAccessoryModalOpen] = useState(false)
  const [accessoryEditData, setAccessoryEditData] = useState(null)
  const [accessoryIsEdit, setAccessoryIsEdit] = useState(false)

  const [printDateModal, setPrintDateModal] = useState(null)
  const [customReceiptModal, setCustomReceiptModal] = useState(false)
  const [isCustomReceiptDownloading, setIsCustomReceiptDownloading] = useState(false)

  const handlePdfDownload = async (key, options) => {
    if (activePdfKey !== null) return
    setActivePdfKey(key)
    try {
      await downloadAndOpenFile(options)
    } finally {
      setActivePdfKey(null)
    }
  }

  const openPrintDateModal = (key, url, title, defaultDate = "") => {
    if (activePdfKey !== null) return
    setPrintDateModal({ key, url, title, defaultDate })
  }

  const handlePrintDateConfirm = printDate => {
    const target = printDateModal
    setPrintDateModal(null)
    if (!target) return
    handlePdfDownload(target.key, {
      url: target.url,
      params: { print_date: printDate || "" },
    })
  }

  const handleCustomReceiptPdf = async fields => {
    setCustomReceiptModal(false)
    setIsCustomReceiptDownloading(true)
    try {
      await downloadAndOpenFile({
        url: `/money-receipts/${id}/custom-receipt-pdf`,
        method: "post",
        data: fields,
      })
    } catch (error) {
      handleError(error, "Failed to generate custom receipt PDF.")
    } finally {
      setIsCustomReceiptDownloading(false)
    }
  }

  const handleAccessoryBillDownload = async (key, usageId) => {
    if (activeAccessoryBillKey !== null) return
    setActiveAccessoryBillKey(key)
    try {
      await getAccessoriesUsageBillPdfById(usageId, (loading) => {
        if (loading) setActiveAccessoryBillKey(key)
        else setActiveAccessoryBillKey(null)
      })
    } finally {
      setActiveAccessoryBillKey(null)
    }
  }

  const { hasAnyPermission } = useAuth()
  const canViewActivityLog = hasAnyPermission([ACCESS.ACTIVITY_LOG.VIEW_LIST])
  const canViewAgreementTab = hasAnyPermission([
    ACCESS.MONEY_RECEIPT_AGREEMENT.VIEW,
    ACCESS.MONEY_RECEIPT_AGREEMENT.EDIT,
  ])
  const canViewLoanTab = hasAnyPermission([
    ACCESS.MONEY_RECEIPT_LOAN.VIEW,
    ACCESS.MONEY_RECEIPT_LOAN.EDIT,
    ACCESS.MONEY_RECEIPT_LOAN.DELETE,
  ])
  const canViewPrintTab = hasAnyPermission([
    ACCESS.MONEY_RECEIPT.VIEW,
    ACCESS.MONEY_RECEIPT_LOAN.VIEW,
  ])
  const canViewAfterSaleServiceTab = hasAnyPermission([
    ACCESS.MONEY_RECEIPT_AFTER_SALE_SERVICE.VIEW,
  ])
  const { data, isLoading, refetch } = useMoneyReceipt(id)
  const activityParams = useMemo(
    () => ({
      per_page: 25,
      money_receipt_id: id,
    }),
    [id]
  )
  const {
    data: activityLogsResponse,
    isLoading: isActivityLoading,
    refetch: refetchActivityLogs,
  } = useActivityLogs(activityParams, Boolean(id) && canViewActivityLog)
  const { data: activityDetailData, isLoading: isActivityDetailLoading } =
    useActivityLog(
      selectedActivityId,
      Boolean(selectedActivityId) && canViewActivityLog
    )
  const { mutateAsync: createPayment, isPending: isCreatingPayment } =
    useCreateAccountCustomerPayment()
  const { mutateAsync: editPayment, isPending: isEditingPayment } =
    useEditAccountCustomerPayment()
  const { mutateAsync: deletePayment, isPending: isDeletingPayment } =
    useDeleteAccountCustomerPayment()
  const { mutateAsync: saveLoan, isPending: isSavingLoan } =
    useSaveMoneyReceiptLoan()
  const { mutateAsync: deleteLoan, isPending: isDeletingLoan } =
    useDeleteMoneyReceiptLoan()
  const {
    mutateAsync: editMoneyReceipt,
    isPending: isUpdatingSection,
    reset: resetSectionUpdateState,
  } = useEditMoneyReceipt()
  const { data: paymentViewResponse, isLoading: isPaymentViewLoading } =
    useAccountCustomerPayment(viewPaymentId, Boolean(viewPaymentId))
  const { handleResponse, handleError } = useToastMessage()
  const queryClient = useQueryClient()
  const { data: complimentaryItemsResponse } =
    useAllMoneyReceiptComplimentaryItems({}, true)

  const accessoriesParams = useMemo(
    () => ({ vehicle_id: data?.data?.vehicle_id, all: true }),
    [data?.data?.vehicle_id]
  )
  const { data: accessoriesUsagesResponse, refetch: refetchAccessoriesUsages } =
    useAccessoriesUsages(accessoriesParams, Boolean(data?.data?.vehicle_id))

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )
  const tabs = useMemo(() => {
    const nextTabs = TAB_ITEMS.filter(tab => {
      if (tab.id === "agreement") return canViewAgreementTab
      if (tab.id === "loan") return canViewLoanTab
      if (tab.id === "print") return canViewPrintTab
      if (tab.id === "after-sale-service") return canViewAfterSaleServiceTab
      return true
    })
    if (canViewActivityLog) {
      nextTabs.push({
        id: "activity",
        label: "Activity",
        icon: "bx bx-history",
      })
    }
    return nextTabs
  }, [
    canViewActivityLog,
    canViewAgreementTab,
    canViewLoanTab,
    canViewPrintTab,
    canViewAfterSaleServiceTab,
  ])
  const requestedTab = searchParams.get("tab")
  const defaultTab = tabs.some(tab => tab.id === "payments")
    ? "payments"
    : tabs[0]?.id || "overview"
  const activeTab = tabs.some(tab => tab.id === requestedTab)
    ? requestedTab
    : defaultTab

  useEffect(() => {
    if (!requestedTab || !tabs.some(tab => tab.id === requestedTab)) {
      navigate(`/money-receipts/${id}?tab=${defaultTab}`, { replace: true })
    }
  }, [defaultTab, id, navigate, requestedTab, tabs])

  const moneyReceipt = data?.data ?? {}
  const customerInfo = getReceiptCustomerInfo(moneyReceipt)
  const vehicleInfo = getReceiptVehicleInfo(moneyReceipt)
  const payments = moneyReceipt?.payments ?? []
  const additionalCosts = getAdditionalCosts(moneyReceipt)
  const complimentaryItems = getComplimentaryItems(moneyReceipt)
  const amountPaid = Number(moneyReceipt?.amount_paid ?? 0)
  const totalAmount = Number(moneyReceipt?.total_amount ?? 0)
  const dueAmount = Number(moneyReceipt?.due_amount ?? 0)
  const overpaidAmount = Number(moneyReceipt?.overpaid_amount ?? 0)
  const collectionProgress = getCollectionProgress(amountPaid, totalAmount)
  const canEditReceipt = moneyReceipt?.status === "active"
  const canManagePayments = moneyReceipt?.status === "active"
  const canManageLoan = moneyReceipt?.status === "active"
  const paymentStatusLabel = formatLabelText(
    moneyReceipt?.payment_status ?? "pending"
  )
  const activityLogs = activityLogsResponse?.data?.data || []
  const loanInformation = getLoanInformation(moneyReceipt)
  const loanTotals = getLoanTotals(loanInformation)
  const hasLoanInformation = Boolean(moneyReceipt?.loan_information)
  const availableComplimentaryItems = Array.isArray(complimentaryItemsResponse?.data)
    ? complimentaryItemsResponse.data
    : complimentaryItemsResponse?.data?.data || []

  const complimentaryItemOptions = useMemo(() => {
    const selectedIds = (sectionFormValues.complimentary_items || [])
      .map(item => item.service_list_id || item.id)
      .filter(Boolean)
      .map(id => String(id))
    const selectedNames = (sectionFormValues.complimentary_items || [])
      .map(item => item.name)
      .filter(Boolean)
      .map(name => String(name).trim().toLowerCase())
    const additionalCostNames = (sectionFormValues.additional_costs || [])
      .map(item => item.name)
      .filter(Boolean)
      .map(name => String(name).trim().toLowerCase())
    return (availableComplimentaryItems || [])
      .filter(
        item =>
          !selectedIds.includes(String(item.id)) &&
          !selectedNames.includes(
            String(item.name || "")
              .trim()
              .toLowerCase()
          ) &&
          !additionalCostNames.includes(
            String(item.name || "")
              .trim()
              .toLowerCase()
          )
      )
      .map(item => ({
        value: String(item.id),
        label: item.name,
        item,
      }))
  }, [
    availableComplimentaryItems,
    sectionFormValues.complimentary_items,
    sectionFormValues.additional_costs,
  ])

  const selectedComplimentaryOptions = useMemo(
    () =>
      (sectionFormValues.complimentary_items || []).map(item => {
        const itemId = item.service_list_id || item.id
        const opt = complimentaryItemOptions.find(
          o => String(o.value) === String(itemId)
        )
        if (opt) return opt
        return {
          value: item.name,
          label: item.custom_name || item.name,
        }
      }),
    [sectionFormValues.complimentary_items, complimentaryItemOptions]
  )

  const costSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "38px",
      backgroundColor: "#fff",
    }),
    menu: provided => ({ ...provided, zIndex: 9999 }),
    input: provided => ({ ...provided, backgroundColor: "transparent" }),
    singleValue: provided => ({ ...provided, backgroundColor: "transparent" }),
  }

  const complimentarySelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "38px",
      backgroundColor: "#fff",
    }),
    menu: provided => ({ ...provided, zIndex: 9999 }),
    input: provided => ({ ...provided, backgroundColor: "transparent" }),
    singleValue: provided => ({ ...provided, backgroundColor: "transparent" }),
    multiValue: provided => ({ ...provided, backgroundColor: "#e9ecef" }),
    multiValueLabel: provided => ({
      ...provided,
      backgroundColor: "transparent",
    }),
    multiValueRemove: provided => ({
      ...provided,
      backgroundColor: "transparent",
    }),
  }

  const costNameOptions = useMemo(() => {
    const otherSelectedNames = (sectionFormValues.additional_costs || [])
      .map(item => item.name)
      .filter(Boolean)
      .map(name => String(name).trim().toLowerCase())
    const complimentaryNames = (sectionFormValues.complimentary_items || [])
      .map(item => item.name)
      .filter(Boolean)
      .map(name => String(name).trim().toLowerCase())
    return (availableComplimentaryItems || [])
      .filter(
        item =>
          !otherSelectedNames.includes(
            String(item.name || "")
              .trim()
              .toLowerCase()
          ) &&
          !complimentaryNames.includes(
            String(item.name || "")
              .trim()
              .toLowerCase()
          )
      )
      .map(item => ({
        value: item.name,
        label: item.name,
      }))
  }, [
    availableComplimentaryItems,
    sectionFormValues.additional_costs,
    sectionFormValues.complimentary_items,
  ])

  const deliveryInfoRows = [
    {
      label: "Delivery Date",
      value:
        formatReadableDate(moneyReceipt?.delivery_date, "MMM d, y") || "N/A",
    },
    {
      label: "Delivery Services",
      value: `${moneyReceipt?.delivery_services?.length || 0} Pcs`,
    },
    {
      label: "Delivery Papers",
      value: `${moneyReceipt?.delivery_papers?.length || 0} Pcs`,
    },
  ]

  const agreementRows = [
    { label: "Car Price", value: formatBDT(moneyReceipt?.car_price) },
    {
      label: "Registration Cost",
      value: formatBDT(moneyReceipt?.registration_cost),
    },
    { label: "Discount", value: formatBDT(vehicleInfo.discount) },
    ...additionalCosts.map((item, index) => ({
      label: item?.name || `Additional Cost ${index + 1}`,
      value: formatBDT(item?.amount),
    })),
    { label: "Total", value: formatBDT(totalAmount) },
    { label: "In Words", value: formatWords(totalAmount) },
  ]

  const complimentaryDisplayItems = complimentaryItems.map(item => ({
    label: `${item.custom_name || item.name || "Unnamed Item"} (Complimentary)`,
    value: "Included",
  }))

  const customerRows = [
    { label: "Type", value: customerInfo.customerType },
    {
      label: "Name",
      value: (
        <CustomerLink
          customer={moneyReceipt?.customer}
          fallbackName={customerInfo.name}
        />
      ),
    },
    { label: "Mobile", value: customerInfo.mobile },
    { label: "Email", value: customerInfo.email },
    ...(customerInfo.customerTypeKey === "service"
      ? [
          { label: "Designation", value: customerInfo.designation },
          { label: "Company Name", value: customerInfo.organization },
        ]
      : []),
    ...(customerInfo.customerTypeKey === "banker"
      ? [
          { label: "Designation", value: customerInfo.designation },
          { label: "Bank Name", value: customerInfo.organization },
          { label: "Branch Name", value: customerInfo.branchName },
          { label: "Account Name", value: customerInfo.accountName },
        ]
      : []),
    { label: "Address", value: customerInfo.address },
  ]

  const carRows = [
    {
      label: "Stock ID",
      value: (
        <VehicleLink
          vehicle={moneyReceipt?.vehicle}
          name={vehicleInfo.stockId}
        />
      ),
    },
    {
      label: "Vehicle Name",
      value: (
        <VehicleLink
          vehicle={moneyReceipt?.vehicle}
          name={vehicleInfo.vehicleName}
        />
      ),
    },
    { label: "Chassis Number", value: vehicleInfo.chassisNo },
    { label: "Engine Number", value: vehicleInfo.fullEngine },
    { label: "BRTA Reg.", value: vehicleInfo.brtaReg },
    { label: "Fuel", value: vehicleInfo.fuel },
    { label: "Color", value: vehicleInfo.color },
    { label: "CC", value: vehicleInfo.cc },
    { label: "Year", value: vehicleInfo.year },
  ]

  const documentRows = [
    { label: "MR No.", value: moneyReceipt?.mr_no },
    {
      label: "Issued Date",
      value: formatReadableDate(moneyReceipt?.issued_date, "MMM d, y"),
    },
    { label: "Quotation", value: moneyReceipt?.quotation?.qid },
    {
      label: "Receipt Status",
      value: formatLabelText(moneyReceipt?.status),
    },
    { label: "Showroom", value: moneyReceipt?.showroom?.name },
    {
      label: "Created By",
      value: moneyReceipt?.created_by?.name ?? moneyReceipt?.createdBy?.name,
    },
    {
      label: "Updated By",
      value: moneyReceipt?.updated_by?.name ?? moneyReceipt?.updatedBy?.name,
    },
  ]

  const setTab = tabId => navigate(`/money-receipts/${id}?tab=${tabId}`)
  const openReceiptEditPage = () =>
    navigate(`/money-receipts/${id}/edit`, {
      state: { from: location },
    })

  const closeSectionEditModal = () => {
    resetSectionUpdateState()
    setSectionEditType(null)
    setSectionFormValues({})
    setSectionFormErrors({})
  }

  const openSectionEditModal = sectionType => {
    resetSectionUpdateState()
    setSectionEditType(sectionType)
    setSectionFormValues(
      getSectionInitialValues(
        moneyReceipt,
        sectionType,
        customerInfo,
        vehicleInfo
      )
    )
    setSectionFormErrors({})
  }

  const togglePaymentModal = () => {
    setPaymentModalOpen(open => {
      const nextOpen = !open
      if (!nextOpen) {
        setSelectedPayment(null)
        setPaymentModalMode("payment")
      }
      return nextOpen
    })
  }

  const handleAddPayment = async payload => {
    try {
      const response = await createPayment(payload)
      handleResponse(
        response,
        payload.amount < 0
          ? "Refund added successfully."
          : "Payment added successfully.",
        true
      )
      await refetch()
    } catch (error) {
      handleError(
        error,
        payload.amount < 0 ? "Failed to add refund." : "Failed to add payment."
      )
      throw error
    }
  }

  const handleEditPayment = async payload => {
    try {
      const response = await editPayment(payload)
      handleResponse(
        response,
        payload.amount < 0
          ? "Refund updated successfully."
          : "Payment updated successfully.",
        true
      )
      await refetch()
    } catch (error) {
      handleError(
        error,
        payload.amount < 0
          ? "Failed to update refund."
          : "Failed to update payment."
      )
      throw error
    }
  }

  const handleDeletePayment = async paymentId => {
    if (!window.confirm("Delete this payment?")) return

    try {
      const response = await deletePayment(paymentId)
      handleResponse(response, "Payment deleted successfully.", true)
      await refetch()
    } catch (error) {
      handleError(error, "Failed to delete payment.")
    }
  }

  const handleSaveLoan = async payload => {
    try {
      const response = await saveLoan({
        id,
        loan_information: payload,
      })
      handleResponse(response, "Loan information saved successfully.", true)
      await Promise.all([
        refetch(),
        refetchActivityLogs?.(),
        queryClient.invalidateQueries({ queryKey: ["account-bank-account"] }),
      ])
    } catch (error) {
      handleError(error, "Failed to save loan information.")
      throw error
    }
  }

  const handleDeleteLoan = async () => {
    if (!window.confirm("Delete this loan information?")) return

    try {
      await deleteLoan(id)
      await Promise.all([refetch(), refetchActivityLogs?.()])
    } catch (error) {
      handleError(error, "Failed to delete loan information.")
    }
  }

  const buildLoanPdfParams = (options = {}) => {
    const params = {}
    if (options.print_date !== undefined) {
      params.print_date = options.print_date ?? ""
    }
    if (options.with_pad !== undefined) {
      params.with_pad = options.with_pad ? 1 : 0
    }
    if (options.self_signature !== undefined) {
      params.self_signature = options.self_signature ? 1 : 0
    }
    if (options.bank_account_id !== undefined && options.bank_account_id !== null) {
      params.bank_account_id = options.bank_account_id
    }
    return params
  }

  const handleLoanPdfDownload = async (documentKey, options = {}) => {
    try {
      setActiveLoanPdfKey(documentKey)
      await downloadAndOpenFile({
        url: `/money-receipts/${id}/loan-pdf/${documentKey}`,
        params: buildLoanPdfParams(options),
      })
    } catch (error) {
      handleError(error)
    } finally {
      setActiveLoanPdfKey(null)
    }
  }

  const handleLoanPdfGroupDownload = async (options = {}) => {
    try {
      setActiveLoanPdfKey("group")
      await downloadAndOpenFile({
        url: `/money-receipts/${id}/loan-pdf-group`,
        params: buildLoanPdfParams(options),
      })
    } catch (error) {
      handleError(error)
    } finally {
      setActiveLoanPdfKey(null)
    }
  }

  const openAddPaymentModal = () => {
    setSelectedPayment(null)
    setPaymentModalMode("payment")
    setPaymentModalOpen(true)
  }

  const openRefundModal = () => {
    setSelectedPayment(null)
    setPaymentModalMode("refund")
    setPaymentModalOpen(true)
  }

  const openEditPaymentModal = payment => {
    setSelectedPayment(payment)
    setPaymentModalMode(Number(payment?.amount || 0) < 0 ? "refund" : "payment")
    setPaymentModalOpen(true)
  }

  const handleSectionFieldChange = event => {
    const { name, value } = event.target
    setSectionFormValues(current => {
      const nextValues = {
        ...current,
        [name]: value,
      }

      if (name === "customer_type") {
        if (value === "general") {
          nextValues.designation = ""
          nextValues.company_name = ""
          nextValues.bank_name = ""
          nextValues.branch_name = ""
        } else if (value === "service") {
          nextValues.bank_name = ""
          nextValues.branch_name = ""
        } else if (value === "banker") {
          nextValues.company_name = ""
        }
      }

      return nextValues
    })
    setSectionFormErrors(current => ({
      ...current,
      [name]: "",
    }))
  }

  const handleAdditionalCostChange = (index, field, value) => {
    setSectionFormValues(current => ({
      ...current,
      additional_costs: (current.additional_costs || []).map(
        (item, itemIndex) =>
          itemIndex === index
            ? {
                ...item,
                [field]: value,
              }
            : item
      ),
    }))
    setSectionFormErrors(current => ({
      ...current,
      [`additional_cost_${field}_${index}`]: "",
    }))
  }

  const addAdditionalCostRow = () => {
    setSectionFormValues(current => ({
      ...current,
      additional_costs: [
        ...(current.additional_costs || []),
        createEmptyAdditionalCost(),
      ],
    }))
  }

  const removeAdditionalCostRow = index => {
    setSectionFormValues(current => ({
      ...current,
      additional_costs:
        (current.additional_costs || []).filter(
          (_, itemIndex) => itemIndex !== index
        ).length > 0
          ? (current.additional_costs || []).filter(
              (_, itemIndex) => itemIndex !== index
            )
          : [createEmptyAdditionalCost()],
    }))
  }

  const handleComplimentaryItemsChange = selectedOptions => {
    const items = (selectedOptions || []).map(opt => {
      if (opt.item) return { service_list_id: opt.item.id, name: opt.item.name }
      return { name: opt.value }
    })
    setSectionFormValues(current => ({
      ...current,
      complimentary_items: items,
    }))
  }

  const handleComplimentaryItemsCreate = inputValue => {
    if (!inputValue || inputValue.trim() === "") return

    setSectionFormValues(current => {
      const currentItems = current.complimentary_items || []
      const normalizedValue = inputValue
        .trim()
        .split(/\s+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ")
      const exists = currentItems.some(
        item =>
          String(item?.name || "").toLowerCase() ===
          normalizedValue.toLowerCase()
      )

      if (exists) return current

      return {
        ...current,
        complimentary_items: [...currentItems, { name: normalizedValue }],
      }
    })
  }

  const handleAdditionalCostNameChangeWithSync = (index, value) => {
    const currentCosts = sectionFormValues.additional_costs || []
    const otherIndexes = currentCosts
      .map((item, i) => (i !== index ? item.name : null))
      .filter(Boolean)

    if (value && otherIndexes.includes(value)) {
      setSectionFormErrors(current => ({
        ...current,
        [`additional_cost_name_${index}`]: "Duplicate cost name",
      }))
      return
    }

    setSectionFormValues(current => ({
      ...current,
      additional_costs: (current.additional_costs || []).map((item, i) =>
        i === index ? { ...item, name: value } : item
      ),
    }))
    setSectionFormErrors(current => ({
      ...current,
      [`additional_cost_name_${index}`]: "",
    }))
  }

  const buildSectionUpdatePayload = (sectionType, values) => {
    const basePdfContent = {
      ...(moneyReceipt?.pdf_content || {}),
      customer_info: {
        ...(moneyReceipt?.pdf_content?.customer_info || {}),
      },
      vehicle_info: {
        ...(moneyReceipt?.pdf_content?.vehicle_info || {}),
      },
      additional_costs: [
        ...(moneyReceipt?.pdf_content?.additional_costs || []),
      ],
    }

    if (sectionType === "agreement") {
      const normalizedAdditionalCosts = (values.additional_costs || [])
        .filter(
          item =>
            String(item?.name || "").trim() || String(item?.amount || "").trim()
        )
        .map(item => ({
          name: String(item?.name || "").trim(),
          amount: Math.max(0, Math.round(Number(item?.amount || 0))),
        }))

      basePdfContent.vehicle_info = {
        ...basePdfContent.vehicle_info,
        discount: Math.max(0, Math.round(Number(values.discount || 0))),
        price: Math.max(0, Math.round(Number(values.car_price || 0))),
        registration_cost: Math.max(
          0,
          Math.round(Number(values.registration_cost || 0))
        ),
      }
      basePdfContent.additional_costs = normalizedAdditionalCosts
    }

    const normalizedComplimentaryItems = (values.complimentary_items || []).map(
      item => {
        if (item.service_list_id) {
          return { service_list_id: item.service_list_id, custom_name: item.custom_name || null }
        }
        return { name: item.name }
      }
    )

    if (sectionType === "customer") {
      basePdfContent.customer_info = {
        ...basePdfContent.customer_info,
        name: String(values.name || "").trim(),
        mobile: String(values.mobile || "").trim(),
        email: String(values.email || "").trim(),
        customer_type: String(values.customer_type || "").trim(),
        designation: String(values.designation || "").trim(),
        company_name: String(values.company_name || "").trim(),
        bank_name: String(values.bank_name || "").trim(),
        branch_name: String(values.branch_name || "").trim(),
        address: String(values.address || "").trim(),
      }
    }

    if (sectionType === "car") {
      basePdfContent.vehicle_info = {
        ...basePdfContent.vehicle_info,
        name: String(values.name || "").trim(),
        chassis_no: String(values.chassis_no || "").trim(),
        full_engine: String(values.full_engine || "").trim(),
        brta_reg: String(values.brta_reg || "").trim(),
        fuel: String(values.fuel || "").trim(),
        color: String(values.color || "").trim(),
        cc: String(values.cc || "").trim(),
        year_model: String(values.year_model || "").trim(),
      }
    }

    return {
      customer_id: moneyReceipt?.customer_id || null,
      vehicle_id: moneyReceipt?.vehicle_id || null,
      quotation_id: moneyReceipt?.quotation_id || null,
      car_price:
        sectionType === "agreement"
          ? Math.max(0, Math.round(Number(values.car_price || 0)))
          : Math.max(0, Math.round(Number(moneyReceipt?.car_price || 0))),
      registration_cost:
        sectionType === "agreement"
          ? Math.max(0, Math.round(Number(values.registration_cost || 0)))
          : Math.max(
              0,
              Math.round(Number(moneyReceipt?.registration_cost || 0))
            ),
      issued_date: moneyReceipt?.issued_date,
      notes: moneyReceipt?.notes || "",
      edit_reason: String(values.edit_reason || "").trim() || null,
      loan_information: moneyReceipt?.loan_information || null,
      pdf_content: basePdfContent,
      complimentary_items: normalizedComplimentaryItems,
    }
  }

  const handleSectionSave = async () => {
    const errors = validateSectionValues(
      sectionEditType,
      sectionFormValues,
      amountPaid
    )
    setSectionFormErrors(errors)

    if (Object.keys(errors).length > 0) {
      return
    }

    try {
      const payload = buildSectionUpdatePayload(
        sectionEditType,
        sectionFormValues
      )
      const response = await editMoneyReceipt({ id, ...payload })
      handleResponse(response, "Money receipt updated successfully.", true)
      if (response?.overpaid_warning) {
        toast.warning(
          `Receipt is now overpaid by ${formatBDT(
            response.overpaid_amount
          )}. Add a refund from the Payments section.`
        )
      }
      closeSectionEditModal()
      await Promise.all([refetch(), refetchActivityLogs?.()])
    } catch (error) {
      handleError(error, "Failed to update money receipt.")
    }
  }

  const leftCardEditAction = (
    <CanView access={[ACCESS.MONEY_RECEIPT.EDIT]}>
      <Button
        color="light"
        size="sm"
        onClick={openReceiptEditPage}
        disabled={!canEditReceipt}
      >
        <i className="bx bx-edit-alt me-1" />
        Edit
      </Button>
    </CanView>
  )

  const sectionModalEditAction = sectionType => (
    <CanView access={[ACCESS.MONEY_RECEIPT.EDIT]}>
      <Button
        color="light"
        size="sm"
        onClick={() => openSectionEditModal(sectionType)}
        disabled={!canEditReceipt}
      >
        <i className="bx bx-edit-alt me-1" />
        Edit
      </Button>
    </CanView>
  )

  const customerSectionType = resolveCustomerTypeKey(
    sectionFormValues.customer_type || "general"
  )
  const isCustomerSectionService = customerSectionType === "service"
  const isCustomerSectionBanker = customerSectionType === "banker"
  const sectionModalTitle =
    sectionEditType === "agreement"
      ? "Edit Booking Agreement"
      : sectionEditType === "customer"
      ? "Edit Customer Information"
      : sectionEditType === "car"
      ? "Edit Car Information"
      : "Edit Section"

  return (
    <PageWrapper
      title="Money Receipt"
      breadcrumbItem="Money Receipt View"
      cardBody={false}
    >
      {isLoading ? (
        <Card className="border-0 shadow-sm">
          <CardBody>Loading...</CardBody>
        </Card>
      ) : (
        <Row>
          <Col lg="4" className="mb-4">
            <ReceiptProfileCard
              moneyReceipt={moneyReceipt}
              customerInfo={customerInfo}
              vehicleInfo={vehicleInfo}
              paymentStatusLabel={paymentStatusLabel}
              collectionProgress={collectionProgress}
              amountPaid={amountPaid}
              totalAmount={totalAmount}
              dueAmount={dueAmount}
              overpaidAmount={overpaidAmount}
              onBack={() => navigate("/money-receipts")}
              onEdit={() =>
                navigate(`/money-receipts/${id}/edit`, {
                  state: { from: location },
                })
              }
              canEditReceipt={canEditReceipt}
            />

            <div className="d-grid gap-4">
              <LeftRailDataCard
                title="Customer Information"
                icon="bx bx-user-circle"
                action={sectionModalEditAction("customer")}
                data={customerRows}
              />

              <LeftRailDataCard
                title="Car Information"
                icon="bx bx-car"
                action={sectionModalEditAction("car")}
                data={carRows}
              />

              {moneyReceipt?.notes ? (
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-3">
                    <SectionHeader
                      icon="bx bx-note"
                      title="Notes"
                      action={leftCardEditAction}
                    />
                    <div className="money-receipt-note">
                      {moneyReceipt.notes}
                    </div>
                  </CardBody>
                </Card>
              ) : null}
            </div>
          </Col>

          <Col lg="8">
            <Nav pills className="nav-pills-custom mb-4 receipt-tab-nav">
              {tabs.map(tab => (
                <NavItem key={tab.id}>
                  <NavLink
                    className={classnames({ active: activeTab === tab.id })}
                    onClick={() => setTab(tab.id)}
                  >
                    <i className={`${tab.icon} me-2`} />
                    {tab.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="agreement">
                <CanView
                  access={[
                    ACCESS.MONEY_RECEIPT_AGREEMENT.VIEW,
                    ACCESS.MONEY_RECEIPT_AGREEMENT.EDIT,
                  ]}
                >
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-3">
                      <SectionHeader
                        icon="bx bx-receipt"
                        title="Booking Agreement"
                        action={
                          <div className="d-flex flex-wrap gap-2">
                            <CanView
                              access={[ACCESS.MONEY_RECEIPT_AGREEMENT.VIEW]}
                            >
                              <Button
                                color="info"
                                size="sm"
                                onClick={() =>
                                  handlePdfDownload("agreement", {
                                    url: `/money-receipts/${id}/agreement-pdf`,
                                  })
                                }
                                disabled={activePdfKey !== null}
                              >
                                <i
                                  className={`bx ${
                                    activePdfKey === "agreement"
                                      ? "bx-loader bx-spin"
                                      : "bx-file"
                                  } me-1`}
                                />
                                {activePdfKey === "agreement"
                                  ? "Generating..."
                                  : "Agreement PDF"}
                              </Button>
                            </CanView>

                            <CanView access={[ACCESS.MONEY_RECEIPT.VIEW]}>
                              <Button
                                color="secondary"
                                size="sm"
                                onClick={() =>
                                  handlePdfDownload("work-order", {
                                    url: `/money-receipts/${id}/work-order-pdf`,
                                  })
                                }
                                disabled={activePdfKey !== null}
                              >
                                <i
                                  className={`bx ${
                                    activePdfKey === "work-order"
                                      ? "bx-loader bx-spin"
                                      : "bx-clipboard"
                                  } me-1`}
                                />
                                {activePdfKey === "work-order"
                                  ? "Generating..."
                                  : "Work Order"}
                              </Button>
                            </CanView>

                            <CanView
                              access={[ACCESS.MONEY_RECEIPT_AGREEMENT.EDIT]}
                            >
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() =>
                                  openSectionEditModal("agreement")
                                }
                                disabled={!canEditReceipt}
                              >
                                <i className="bx bx-edit-alt me-1" />
                                Edit
                              </Button>
                            </CanView>
                          </div>
                        }
                      />

                      <div className="rounded-3 border bg-white p-3">
                        <DataCard
                          title="Agreement Summary"
                          icon="bx bx-detail"
                          data={[
                            ...agreementRows,
                            ...complimentaryDisplayItems,
                          ]}
                        />
                      </div>

                      {complimentaryItems.length > 0 && (
                        <div className="mt-3">
                          <h6 className="mb-2">
                            <i className="bx bx-gift me-1" />
                            Complimentary Items
                          </h6>
                          <div className="d-flex flex-wrap gap-2">
                            {complimentaryItems.map((item, index) => (
                              <Badge
                                key={item.id || `comp-${index}`}
                                className="badge-soft-success border-0 px-2 py-1"
                              >
                                {item.custom_name ||
                                  item.name ||
                                  "Unnamed Item"}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </CanView>
              </TabPane>

              <TabPane tabId="payments">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-3">
                    <SectionHeader
                      icon="bx bx-credit-card-front"
                      title="Payments"
                      action={
                        <div className="d-flex flex-wrap gap-2">
                          <CanView access={[ACCESS.MONEY_RECEIPT.VIEW]}>
                            <Button
                              color="info"
                              size="sm"
                              onClick={() =>
                                openPrintDateModal(
                                  "money-receipt",
                                  `/money-receipts/${id}/pdf`,
                                  "Money Receipt — Print Date",
                                  moneyReceipt?.issued_date
                                )
                              }
                              disabled={activePdfKey !== null}
                            >
                              <i
                                className={`bx ${
                                  activePdfKey === "money-receipt"
                                    ? "bx-loader bx-spin"
                                    : "bx-download"
                                } me-1`}
                              />
                              {activePdfKey === "money-receipt"
                                ? "Generating..."
                                : "Money Receipt"}
                            </Button>
                          </CanView>

                          <CanView access={[ACCESS.MONEY_RECEIPT.VIEW]}>
                            <Button
                              color="secondary"
                              size="sm"
                              onClick={() =>
                                openPrintDateModal(
                                  "delivery-challan",
                                  `/money-receipts/${id}/customer-delivery-challan-pdf`,
                                  "Delivery Challan — Print Date",
                                  moneyReceipt?.issued_date
                                )
                              }
                              disabled={activePdfKey !== null}
                            >
                              <i
                                className={`bx ${
                                  activePdfKey === "delivery-challan"
                                    ? "bx-loader bx-spin"
                                    : "bx-package"
                                } me-1`}
                              />
                              {activePdfKey === "delivery-challan"
                                ? "Generating..."
                                : "Delivery Challan"}
                            </Button>
                          </CanView>


                          <CanView access={[ACCESS.MONEY_RECEIPT.VIEW]}>
                            <Button
                              color="success"
                              outline
                              size="sm"
                              onClick={() => setCustomReceiptModal(true)}
                              disabled={activePdfKey !== null || isCustomReceiptDownloading}
                            >
                              <i
                                className={`mdi ${
                                  isCustomReceiptDownloading
                                    ? "mdi-loading mdi-spin"
                                    : "mdi-file-edit-outline"
                                } me-1`}
                              />
                              {isCustomReceiptDownloading
                                ? "Generating..."
                                : "Custom Receipt"}
                            </Button>
                          </CanView>

                          <CanView
                            access={[
                              ACCESS.MONEY_RECEIPT.ADD_PAYMENT,
                              ACCESS.ACCOUNT_CUSTOMER_PAYMENT.ADD,
                            ]}
                          >
                            <Button
                              color="danger"
                              size="sm"
                              onClick={openRefundModal}
                              disabled={!canManagePayments || amountPaid <= 0}
                            >
                              <i className="bx bx-undo me-1" />
                              Add Refund
                            </Button>
                          </CanView>

                          <CanView
                            access={[
                              ACCESS.MONEY_RECEIPT.ADD_PAYMENT,
                              ACCESS.ACCOUNT_CUSTOMER_PAYMENT.ADD,
                            ]}
                          >
                            <Button
                              color="primary"
                              size="sm"
                              onClick={openAddPaymentModal}
                              disabled={!canManagePayments}
                            >
                              <i className="bx bx-plus me-1" />
                              Add Payment
                            </Button>
                          </CanView>
                        </div>
                      }
                    />

                    <div className="money-receipt-payment-banner mb-3">
                      <div>
                        <small className="text-muted d-block">Total Paid</small>
                        <span className="fw-semibold">
                          {formatBDT(amountPaid)}
                        </span>
                      </div>
                      <div>
                        <small className="text-muted d-block">Due</small>
                        <span className="fw-semibold">
                          {formatBDT(dueAmount)}
                        </span>
                      </div>
                      <div className="money-receipt-payment-banner__words">
                        <small className="text-muted d-block">In Words</small>
                        <span className="fw-medium">
                          {formatWords(amountPaid)}
                        </span>
                      </div>
                    </div>

                    {payments.length ? (
                      <div className="table-responsive">
                        <Table className="align-middle mb-0">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Payment Date</th>
                              <th>Payment Mode</th>
                              <th>Remarks</th>
                              <th>Amount</th>
                              <th>Created</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payments.map((payment, index) => (
                              <tr key={payment.id}>
                                <td>{index + 1}</td>
                                <td>
                                  {formatReadableDate(
                                    payment?.received_date,
                                    "MMM d, y"
                                  ) || "N/A"}
                                </td>
                                <td>
                                  <div className="d-flex flex-column gap-1">
                                    <span>
                                      {payment?.payment_method?.name ?? "N/A"}
                                    </span>
                                    <span className="small text-muted">
                                      {Number(payment?.amount || 0) < 0
                                        ? "Refund"
                                        : "Payment"}
                                    </span>
                                    {payment?.pre_order_id ? (
                                      <Badge
                                        className="badge-soft-info border-0 align-self-start"
                                        title="This payment was collected as a booking deposit and carried forward from the pre-order"
                                      >
                                        <i className="bx bx-link-alt me-1" />
                                        Carried from Pre-Order
                                      </Badge>
                                    ) : null}
                                  </div>
                                </td>
                                <td className="money-receipt-table-notes">
                                  {payment?.notes ?? "-"}
                                </td>
                                <td
                                  className={
                                    Number(payment?.amount || 0) < 0
                                      ? "text-danger fw-semibold"
                                      : "text-success fw-semibold"
                                  }
                                >
                                  {formatBDT(payment?.amount)}
                                </td>
                                <td>
                                  {formatReadableDate(
                                    payment?.created_at,
                                    "MMM d, y"
                                  ) ||
                                    formatReadableDate(
                                      payment?.received_date,
                                      "MMM d, y"
                                    ) ||
                                    "N/A"}
                                </td>
                                <td>
                                  <div className="d-flex flex-wrap gap-2">
                                    <CanView
                                      access={[
                                        ACCESS.ACCOUNT_CUSTOMER_PAYMENT
                                          .VIEW_LIST,
                                      ]}
                                    >
                                      <Button
                                        color="light"
                                        size="sm"
                                        title="View payment"
                                        onClick={() =>
                                          setViewPaymentId(payment.id)
                                        }
                                      >
                                        <i className="bx bx-show-alt" />
                                      </Button>
                                    </CanView>

                                    <CanView
                                      access={[
                                        ACCESS.ACCOUNT_CUSTOMER_PAYMENT
                                          .VIEW_LIST,
                                      ]}
                                    >
                                      <Button
                                        color="secondary"
                                        size="sm"
                                        title="Download payment PDF"
                                        onClick={() =>
                                          openPrintDateModal(
                                            `payment-${payment.id}`,
                                            `/account-customer-payments/${payment.id}/receipt`,
                                            "Payment Receipt — Print Date",
                                            payment?.received_date
                                          )
                                        }
                                        disabled={activePdfKey !== null}
                                      >
                                        <i
                                          className={`bx ${
                                            activePdfKey ===
                                            `payment-${payment.id}`
                                              ? "bx-loader bx-spin"
                                              : "bx-download"
                                          }`}
                                        />
                                      </Button>
                                    </CanView>

                                    <CanView
                                      access={[
                                        ACCESS.ACCOUNT_CUSTOMER_PAYMENT.EDIT,
                                      ]}
                                    >
                                      <Button
                                        color="warning"
                                        size="sm"
                                        onClick={() =>
                                          openEditPaymentModal(payment)
                                        }
                                        disabled={!canManagePayments}
                                      >
                                        <i className="bx bx-pencil" />
                                      </Button>
                                    </CanView>

                                    <CanView
                                      access={[
                                        ACCESS.MONEY_RECEIPT.REMOVE_PAYMENT,
                                        ACCESS.ACCOUNT_CUSTOMER_PAYMENT.DELETE,
                                      ]}
                                    >
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() =>
                                          handleDeletePayment(payment.id)
                                        }
                                        disabled={
                                          isDeletingPayment ||
                                          !canManagePayments
                                        }
                                      >
                                        <i className="bx bx-trash" />
                                      </Button>
                                    </CanView>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="money-receipt-empty-state">
                        No payments recorded yet for this receipt.
                      </div>
                    )}
                  </CardBody>
                </Card>
              </TabPane>

              <TabPane tabId="loan">
                <CanView
                  access={[
                    ACCESS.MONEY_RECEIPT_LOAN.VIEW,
                    ACCESS.MONEY_RECEIPT_LOAN.EDIT,
                    ACCESS.MONEY_RECEIPT_LOAN.DELETE,
                  ]}
                >
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-3">
                      <SectionHeader
                        icon="bx bx-buildings"
                        title="Bank Loan Information"
                        action={
                          <div className="d-flex flex-wrap gap-2">
                            <CanView access={[ACCESS.MONEY_RECEIPT_LOAN.EDIT]}>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => setLoanEditModal(true)}
                                disabled={!canManageLoan}
                              >
                                <i className="bx bx-edit-alt me-1" />
                                Edit
                              </Button>
                            </CanView>

                            <CanView
                              access={[ACCESS.MONEY_RECEIPT_LOAN.DELETE]}
                            >
                              <Button
                                color="danger"
                                size="sm"
                                onClick={handleDeleteLoan}
                                disabled={
                                  !canManageLoan ||
                                  isSavingLoan ||
                                  isDeletingLoan ||
                                  !moneyReceipt?.loan_information
                                }
                              >
                                <i className="bx bx-trash me-1" />
                                {isDeletingLoan ? "Deleting..." : "Delete"}
                              </Button>
                            </CanView>
                          </div>
                        }
                      />

                      <LoanInformationSummary
                        loanInformation={loanInformation}
                      />
                      <LoanInformationDetails
                        loanInformation={loanInformation}
                      />
                      <PayorderReceiverCard
                        loanInformation={loanInformation}
                      />
                      <LoanFormatPreview
                        loanInformation={loanInformation}
                        className="mb-4"
                      />
                      <LoanInformationMetrics
                        loanInformation={loanInformation}
                      />

                      <MoneyReceiptLoanPdfActions
                        title="Loan PDF Documents"
                        groupButtonLabel="Generate Group PDF"
                        emptyMessage="Save loan information first to generate loan PDFs."
                        icon="bx bx-printer"
                        hasLoanInformation={hasLoanInformation}
                        activeLoanPdfKey={activeLoanPdfKey}
                        onLoanPdfDownload={handleLoanPdfDownload}
                        onLoanPdfGroupDownload={handleLoanPdfGroupDownload}
                        className="mt-4 mb-0"
                      />
                    </CardBody>
                  </Card>
                </CanView>
              </TabPane>

              <TabPane tabId="print">
                <Row className="g-4">
                  <CanView access={[ACCESS.MONEY_RECEIPT.VIEW]}>
                    <Col md="6">
                      <Card className="border-0 shadow-sm h-100">
                        <CardBody className="p-3">
                          <SectionHeader
                            icon="bx bx-credit-card-front"
                            title="Payments Print"
                          />
                          <div className="money-receipt-print-grid">
                            <PrintActionButton
                              label="Money Receipt"
                              onClick={() =>
                                openPrintDateModal(
                                  "print-money-receipt",
                                  `/money-receipts/${id}/pdf`,
                                  "Money Receipt — Print Date",
                                  moneyReceipt?.issued_date
                                )
                              }
                              disabled={activePdfKey !== null}
                              loading={activePdfKey === "print-money-receipt"}
                            />
                            <PrintActionButton
                              label="Custom Receipt"
                              onClick={() => setCustomReceiptModal(true)}
                              disabled={activePdfKey !== null || isCustomReceiptDownloading}
                              loading={isCustomReceiptDownloading}
                            />
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </CanView>

                  <CanView access={[ACCESS.MONEY_RECEIPT.VIEW]}>
                    <Col md="6">
                      <Card className="border-0 shadow-sm h-100">
                        <CardBody className="p-3">
                          <SectionHeader
                            icon="bx bx-receipt"
                            title="Booking Agreement Print"
                          />
                          <div className="money-receipt-print-grid">
                            <CanView
                              access={[ACCESS.MONEY_RECEIPT_AGREEMENT.VIEW]}
                            >
                              <PrintActionButton
                                label="Agreement PDF"
                                onClick={() =>
                                  handlePdfDownload("print-agreement", {
                                    url: `/money-receipts/${id}/agreement-pdf`,
                                  })
                                }
                                disabled={activePdfKey !== null}
                                loading={activePdfKey === "print-agreement"}
                              />
                            </CanView>
                            <PrintActionButton
                              label="Work Order"
                              onClick={() =>
                                handlePdfDownload("work-order", {
                                  url: `/money-receipts/${id}/work-order-pdf`,
                                })
                              }
                              disabled={activePdfKey !== null}
                              loading={activePdfKey === "work-order"}
                            />
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </CanView>

                  <Col md="6">
                    <MoneyReceiptLoanPdfActions
                      title="Loan Print"
                      groupButtonLabel="Generate Group Loan PDF"
                      emptyMessage="Loan information required before generating loan PDFs."
                      icon="bx bx-duplicate"
                      hasLoanInformation={hasLoanInformation}
                      activeLoanPdfKey={activeLoanPdfKey}
                      onLoanPdfDownload={handleLoanPdfDownload}
                      onLoanPdfGroupDownload={handleLoanPdfGroupDownload}
                      className="h-100"
                    />
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="after-sale-service">
                <CanView access={[ACCESS.MONEY_RECEIPT_AFTER_SALE_SERVICE.VIEW]}>
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-3">
                      <SectionHeader
                        icon="bx bx-wrench"
                        title="After-Sales Service & Car Maintenance"
                      />
                      <p className="text-muted mb-3">
                        Aurobil Cars provides 2 years of complimentary general
                        service support with every reconditioned car, plus paid
                        maintenance for all Japanese vehicles. Download the full
                        after-sales service terms &amp; conditions below.
                      </p>
                      <div className="money-receipt-print-grid">
                        <PrintActionButton
                          label="After-Sales Service Terms PDF"
                          onClick={() =>
                            handlePdfDownload("after-sale-service", {
                              url: `/money-receipts/${id}/after-sale-service-pdf`,
                            })
                          }
                          disabled={activePdfKey !== null}
                          loading={activePdfKey === "after-sale-service"}
                        />
                      </div>
                    </CardBody>
                  </Card>

                  <Card className="border-0 shadow-sm mt-3">
                    <CardBody className="p-3">
                      <SectionHeader
                        icon="bx bx-cart-add"
                        title="Accessories Usage"
                        action={
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => {
                              setAccessoryEditData(null)
                              setAccessoryIsEdit(false)
                              setAccessoryModalOpen(true)
                            }}
                          >
                            <i className="bx bx-plus me-1" />
                            Add Accessory Usage
                          </Button>
                        }
                      />

                      {accessoriesUsagesResponse?.data && accessoriesUsagesResponse.data.length > 0 ? (
                        <div className="table-responsive mt-2">
                          <Table className="align-middle mb-0" size="sm">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Employee</th>
                                <th>Accessories</th>
                                <th className="text-end">Invoice Total</th>
                                <th>Created</th>
                                <th className="text-center">Invoice</th>
                              </tr>
                            </thead>
                            <tbody>
                              {accessoriesUsagesResponse.data.map(usage => (
                                <tr key={usage.id}>
                                  <td>{formatReadableDate(usage.usage_date)}</td>
                                  <td>{usage.usage_by?.name || "-"}</td>
                                  <td>
                                    {usage.items?.map(item => (
                                      <Badge key={item.id} color="info" className="me-1">
                                        {item.accessory?.name} x{item.quantity}
                                      </Badge>
                                    ))}
                                  </td>
                                  <td className="text-end">
                                    {formatBDT(calculateBillTotal(usage))}
                                    {usage.bill_type === "package" && (
                                      <Badge color="secondary" className="ms-1">Package</Badge>
                                    )}
                                  </td>
                                  <td>{formatReadableDate(usage.created_at)}</td>
                                  <td className="text-center">
                                    <Button
                                      color="-link"
                                      size="sm"
                                      className="p-0"
                                      onClick={() => handleAccessoryBillDownload(`bill-${usage.id}`, usage.id)}
                                      disabled={activeAccessoryBillKey === `bill-${usage.id}`}
                                      title="Download Bill"
                                    >
                                      {activeAccessoryBillKey === `bill-${usage.id}` ? (
                                        <i className="bx bx-loader bx-spin" />
                                      ) : (
                                        <i className="bx bx-download text-primary" />
                                      )}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-muted small py-3 text-center">
                          No accessories usage records found for this vehicle.
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </CanView>
              </TabPane>

              <TabPane tabId="activity">
                <CanView access={[ACCESS.ACTIVITY_LOG.VIEW_LIST]}>
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-3">
                      <SectionHeader
                        icon="bx bx-history"
                        title="Activity History"
                        action={
                          <Badge color="light" className="text-dark">
                            {activityLogs.length} entries
                          </Badge>
                        }
                      />

                      {isActivityLoading ? (
                        <div className="text-muted small">
                          Loading activity history...
                        </div>
                      ) : activityLogs.length === 0 ? (
                        <div className="money-receipt-empty-state">
                          No activity history found for this receipt.
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <Table className="align-middle mb-0">
                            <thead>
                              <tr>
                                <th>Timestamp</th>
                                <th>Module</th>
                                <th>Action</th>
                                <th>Subject</th>
                                <th>User</th>
                                <th>Summary</th>
                                <th className="text-end">View</th>
                              </tr>
                            </thead>
                            <tbody>
                              {activityLogs.map(log => {
                                const badge = getActionBadgeConfig(log?.action)

                                return (
                                  <tr key={log.id}>
                                    <td className="money-receipt-activity-time">
                                      {formatReadableDate(log?.created_at) ||
                                        "-"}
                                    </td>
                                    <td>
                                      <span className="money-receipt-activity-module">
                                        {formatLabelText(
                                          log?.module || "activity"
                                        )}
                                      </span>
                                    </td>
                                    <td>
                                      <Badge color={badge.color}>
                                        {badge.label}
                                      </Badge>
                                    </td>
                                    <td>
                                      <div className="money-receipt-activity-primary">
                                        {getActivitySubject(log)}
                                      </div>
                                      <div className="money-receipt-activity-secondary">
                                        #{log?.id || "-"}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="money-receipt-activity-primary">
                                        {getActivityActor(log)}
                                      </div>
                                      <div className="money-receipt-activity-secondary">
                                        {log?.showroom_display ||
                                          log?.showroom?.name ||
                                          "N/A"}
                                      </div>
                                    </td>
                                    <td className="money-receipt-activity-summary">
                                      {getActivitySummary(log)}
                                    </td>
                                    <td className="text-end">
                                      <Button
                                        color="light"
                                        size="sm"
                                        onClick={() =>
                                          setSelectedActivityId(log.id)
                                        }
                                      >
                                        <i className="bx bx-show-alt me-1" />
                                        View
                                      </Button>
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </CanView>
              </TabPane>
            </TabContent>
          </Col>
        </Row>
      )}

      <MoneyReceiptSectionEditModal
        modal={Boolean(sectionEditType)}
        toggleModal={closeSectionEditModal}
        title={sectionModalTitle}
        onSubmit={handleSectionSave}
        isSubmitting={isUpdatingSection}
      >
        {Number(amountPaid || 0) > 0 ? (
          <Alert color="warning" className="mb-3">
            This receipt already has payments. Please provide an edit reason for
            audit tracking.
          </Alert>
        ) : null}

        {sectionEditType === "agreement" ? (
          <div className="d-grid gap-3">
            <Row className="g-3">
              <Col md="6">
                <Label className="form-label">Car Price</Label>
                <Input
                  type="number"
                  min="0"
                  name="car_price"
                  value={sectionFormValues.car_price ?? ""}
                  onChange={handleSectionFieldChange}
                  invalid={Boolean(sectionFormErrors.car_price)}
                />
                <FormFeedback>{sectionFormErrors.car_price}</FormFeedback>
              </Col>
              <Col md="6">
                <Label className="form-label">Registration Cost</Label>
                <Input
                  type="number"
                  min="0"
                  name="registration_cost"
                  value={sectionFormValues.registration_cost ?? ""}
                  onChange={handleSectionFieldChange}
                  invalid={Boolean(sectionFormErrors.registration_cost)}
                />
                <FormFeedback>
                  {sectionFormErrors.registration_cost}
                </FormFeedback>
              </Col>
              <Col md="6">
                <Label className="form-label">Discount</Label>
                <Input
                  type="number"
                  min="0"
                  name="discount"
                  value={sectionFormValues.discount ?? ""}
                  onChange={handleSectionFieldChange}
                  invalid={Boolean(sectionFormErrors.discount)}
                />
                <FormFeedback>{sectionFormErrors.discount}</FormFeedback>
              </Col>
            </Row>

            <div className="rounded-3 border bg-white p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Additional Costs</h6>
                <Button color="light" size="sm" onClick={addAdditionalCostRow}>
                  <i className="bx bx-plus me-1" />
                  Add Cost
                </Button>
              </div>
              <div className="d-grid gap-3">
                {(sectionFormValues.additional_costs || []).map(
                  (item, index) => (
                    <Row className="g-2 align-items-start" key={index}>
                      <Col md="6">
                        <CreatableSelect
                          options={costNameOptions}
                          styles={costSelectStyles}
                          value={
                            item?.name
                              ? costNameOptions.find(
                                  o => o.value === item.name
                                ) || {
                                  value: item.name,
                                  label: item.name,
                                }
                              : null
                          }
                          onChange={option =>
                            handleAdditionalCostNameChangeWithSync(
                              index,
                              option ? option.value : ""
                            )
                          }
                          onCreateOption={inputValue =>
                            handleAdditionalCostNameChangeWithSync(
                              index,
                              inputValue
                            )
                          }
                          placeholder="Cost name"
                          isClearable
                        />
                        {sectionFormErrors[`additional_cost_name_${index}`] && (
                          <div className="text-danger small mt-1">
                            {sectionFormErrors[`additional_cost_name_${index}`]}
                          </div>
                        )}
                      </Col>
                      <Col md="4">
                        <Input
                          type="number"
                          min="0"
                          placeholder="Amount"
                          value={item?.amount ?? ""}
                          onChange={event =>
                            handleAdditionalCostChange(
                              index,
                              "amount",
                              event.target.value
                            )
                          }
                          invalid={Boolean(
                            sectionFormErrors[`additional_cost_amount_${index}`]
                          )}
                        />
                        <FormFeedback>
                          {sectionFormErrors[`additional_cost_amount_${index}`]}
                        </FormFeedback>
                      </Col>
                      <Col md="2">
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => removeAdditionalCostRow(index)}
                        >
                          <i className="bx bx-trash" />
                        </Button>
                      </Col>
                    </Row>
                  )
                )}
              </div>
            </div>

            <div className="rounded-3 border bg-white p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Complimentary Items</h6>
              </div>
              <CreatableSelect
                isMulti
                options={complimentaryItemOptions}
                styles={complimentarySelectStyles}
                value={selectedComplimentaryOptions}
                onChange={handleComplimentaryItemsChange}
                onCreateOption={handleComplimentaryItemsCreate}
                placeholder="Select or type complimentary items..."
                isClearable
              />
            </div>
          </div>
        ) : null}

        {sectionEditType === "customer" ? (
          <Row className="g-3">
            <Col md="6">
              <Label className="form-label">Name</Label>
              <Input
                name="name"
                value={sectionFormValues.name ?? ""}
                onChange={handleSectionFieldChange}
                invalid={Boolean(sectionFormErrors.name)}
              />
              <FormFeedback>{sectionFormErrors.name}</FormFeedback>
            </Col>
            <Col md="3">
              <Label className="form-label">Mobile</Label>
              <Input
                name="mobile"
                value={sectionFormValues.mobile ?? ""}
                onChange={handleSectionFieldChange}
                invalid={Boolean(sectionFormErrors.mobile)}
              />
              <FormFeedback>{sectionFormErrors.mobile}</FormFeedback>
            </Col>
            <Col md="3">
              <Label className="form-label">Email</Label>
              <Input
                name="email"
                value={sectionFormValues.email ?? ""}
                onChange={handleSectionFieldChange}
              />
            </Col>
            <Col md="4">
              <Label className="form-label">Customer Type</Label>
              <Input
                type="select"
                name="customer_type"
                value={sectionFormValues.customer_type ?? "general"}
                onChange={handleSectionFieldChange}
                invalid={Boolean(sectionFormErrors.customer_type)}
              >
                {CUSTOMER_TYPE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
              <FormFeedback>{sectionFormErrors.customer_type}</FormFeedback>
            </Col>
            {(isCustomerSectionService || isCustomerSectionBanker) && (
              <Col md="4">
                <Label className="form-label">Designation</Label>
                <Input
                  name="designation"
                  value={sectionFormValues.designation ?? ""}
                  onChange={handleSectionFieldChange}
                  invalid={Boolean(sectionFormErrors.designation)}
                />
                <FormFeedback>{sectionFormErrors.designation}</FormFeedback>
              </Col>
            )}
            {isCustomerSectionService && (
              <Col md="4">
                <Label className="form-label">Company Name</Label>
                <Input
                  name="company_name"
                  value={sectionFormValues.company_name ?? ""}
                  onChange={handleSectionFieldChange}
                  invalid={Boolean(sectionFormErrors.company_name)}
                />
                <FormFeedback>{sectionFormErrors.company_name}</FormFeedback>
              </Col>
            )}
            {isCustomerSectionBanker && (
              <>
                <Col md="4">
                  <Label className="form-label">Bank Name</Label>
                  <Input
                    name="bank_name"
                    value={sectionFormValues.bank_name ?? ""}
                    onChange={handleSectionFieldChange}
                    invalid={Boolean(sectionFormErrors.bank_name)}
                  />
                  <FormFeedback>{sectionFormErrors.bank_name}</FormFeedback>
                </Col>
                <Col md="4">
                  <Label className="form-label">Branch Name</Label>
                  <Input
                    name="branch_name"
                    value={sectionFormValues.branch_name ?? ""}
                    onChange={handleSectionFieldChange}
                    invalid={Boolean(sectionFormErrors.branch_name)}
                  />
                  <FormFeedback>{sectionFormErrors.branch_name}</FormFeedback>
                </Col>
              </>
            )}
            <Col md="12">
              <Label className="form-label">Address</Label>
              <Input
                type="textarea"
                rows={4}
                name="address"
                value={sectionFormValues.address ?? ""}
                onChange={handleSectionFieldChange}
              />
            </Col>
          </Row>
        ) : null}

        {sectionEditType === "car" ? (
          <Row className="g-3">
            <Col md="6">
              <Label className="form-label">Vehicle Name</Label>
              <Input
                name="name"
                value={sectionFormValues.name ?? ""}
                onChange={handleSectionFieldChange}
                invalid={Boolean(sectionFormErrors.name)}
              />
              <FormFeedback>{sectionFormErrors.name}</FormFeedback>
            </Col>
            <Col md="6">
              <Label className="form-label">Chassis Number</Label>
              <Input
                name="chassis_no"
                value={sectionFormValues.chassis_no ?? ""}
                onChange={handleSectionFieldChange}
                invalid={Boolean(sectionFormErrors.chassis_no)}
              />
              <FormFeedback>{sectionFormErrors.chassis_no}</FormFeedback>
            </Col>
            <Col md="6">
              <Label className="form-label">Engine Number</Label>
              <Input
                name="full_engine"
                value={sectionFormValues.full_engine ?? ""}
                onChange={handleSectionFieldChange}
                invalid={Boolean(sectionFormErrors.full_engine)}
              />
              <FormFeedback>{sectionFormErrors.full_engine}</FormFeedback>
            </Col>
            <Col md="4">
              <Label className="form-label">BRTA Reg.</Label>
              <Input
                name="brta_reg"
                value={sectionFormValues.brta_reg ?? ""}
                onChange={handleSectionFieldChange}
              />
            </Col>
            <Col md="4">
              <Label className="form-label">Fuel</Label>
              <Input
                name="fuel"
                value={sectionFormValues.fuel ?? ""}
                onChange={handleSectionFieldChange}
              />
            </Col>
            <Col md="4">
              <Label className="form-label">Color</Label>
              <Input
                name="color"
                value={sectionFormValues.color ?? ""}
                onChange={handleSectionFieldChange}
              />
            </Col>
            <Col md="6">
              <Label className="form-label">CC</Label>
              <Input
                name="cc"
                value={sectionFormValues.cc ?? ""}
                onChange={handleSectionFieldChange}
              />
            </Col>
            <Col md="6">
              <Label className="form-label">Year</Label>
              <Input
                name="year_model"
                value={sectionFormValues.year_model ?? ""}
                onChange={handleSectionFieldChange}
              />
            </Col>
          </Row>
        ) : null}

        {Number(amountPaid || 0) > 0 ? (
          <div className="mt-3">
            <Label className="form-label">Edit Reason</Label>
            <Input
              type="textarea"
              rows={3}
              name="edit_reason"
              value={sectionFormValues.edit_reason ?? ""}
              onChange={handleSectionFieldChange}
              invalid={Boolean(sectionFormErrors.edit_reason)}
            />
            <FormFeedback>{sectionFormErrors.edit_reason}</FormFeedback>
          </div>
        ) : null}
      </MoneyReceiptSectionEditModal>

      <PrintDateModal
        isOpen={Boolean(printDateModal)}
        toggle={() => setPrintDateModal(null)}
        onSubmit={handlePrintDateConfirm}
        isSubmitting={activePdfKey !== null}
        title={printDateModal?.title || "Print Date"}
        defaultDate={printDateModal?.defaultDate || ""}
      />

      <CustomReceiptModal
        isOpen={customReceiptModal}
        toggle={() => setCustomReceiptModal(false)}
        moneyReceipt={moneyReceipt}
        onSubmit={handleCustomReceiptPdf}
        isSubmitting={isCustomReceiptDownloading}
      />

      <PaymentModal
        isOpen={paymentModalOpen}
        toggle={togglePaymentModal}
        moneyReceipt={moneyReceipt}
        payment={selectedPayment}
        defaultEntryMode={paymentModalMode}
        onSubmit={selectedPayment ? handleEditPayment : handleAddPayment}
        isSubmitting={isCreatingPayment || isEditingPayment}
      />

      <AccountCustomerPaymentView
        modal={Boolean(viewPaymentId)}
        toggleModal={() => setViewPaymentId(null)}
        payment={paymentViewResponse?.data}
        loading={isPaymentViewLoading}
      />

      <MoneyReceiptLoanAddEdit
        modal={loanEditModal}
        toggleModal={() => setLoanEditModal(false)}
        moneyReceipt={moneyReceipt}
        initialValues={loanInformation}
        onSubmit={handleSaveLoan}
        isSubmitting={isSavingLoan}
      />

      <CanView access={[ACCESS.ACTIVITY_LOG.VIEW_LIST]}>
        <ActivityLogView
          isOpen={Boolean(selectedActivityId)}
          toggle={() => setSelectedActivityId(null)}
          activityLog={activityDetailData?.data}
          isLoading={isActivityDetailLoading}
        />
      </CanView>

      <AccessoriesUsageAddEdit
        modal={accessoryModalOpen}
        toggleModal={() => setAccessoryModalOpen(false)}
        isEdit={accessoryIsEdit}
        editedData={accessoryEditData}
        preSelectedVehicle={moneyReceipt?.vehicle}
        preSelectedCustomer={moneyReceipt?.customer}
        hideVehicleSelect={true}
        moneyReceiptId={id}
        onSuccess={refetchAccessoriesUsages}
      />
    </PageWrapper>
  )
}

export default MoneyReceiptViewPage
